/**
 * Clients API Client
 *
 * Handles all client management API calls
 * Uses centralized apiClient for consistent API access
 *
 * Follows: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { Make_Authorized_API_Call, type TimComboAPIResponse } from './apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

// Re-export response types for consumers
export type { TimComboAPIResponse };

/**
 * Client interface matching backend schema
 */
export interface Client {
  Client_ID: string;
  Client_Name: string;
  Client_Type: 'OSS' | 'PRO' | 'ENTERPRISE';
  Client_Status?: 'active' | 'suspended' | 'cancelled';
  Billing_Level?: string;
  Billing_Contact_Email?: string | null;
  Contract_Start_Date?: string | null;
  Contract_End_Date?: string | null;
  Create_Date?: string;
  Last_Updated?: string;
  company_count?: number;
  user_count?: number;
}

/**
 * Client with detailed information
 */
export interface ClientDetails extends Client {
  companies: Company[];
  entitlements: any[];
}

export interface Company {
  Company_ID: string;
  Company_Name: string;
  Company_Status: string;
}

/**
 * Create client request
 */
export interface CreateClientRequest {
  Client_ID: string;
  Client_Name: string;
  Client_Type?: 'OSS' | 'PRO' | 'ENTERPRISE';
  Billing_Level?: string;
  Billing_Contact_Email?: string;
  Contract_Start_Date?: string;
  Contract_End_Date?: string;
}

/**
 * Update client request
 */
export interface UpdateClientRequest {
  Client_Name?: string;
  Client_Type?: 'OSS' | 'PRO' | 'ENTERPRISE';
  Billing_Level?: string;
  Billing_Contact_Email?: string;
  Contract_Start_Date?: string;
  Contract_End_Date?: string;
}

/**
 * List clients query parameters
 */
export interface ListClientsParams {
  status?: string;
  tier?: string;
  limit?: number;
  offset?: number;
}

/**
 * List all clients with optional filters
 * GET /api/admin/clients
 */
export async function listClients(params?: ListClientsParams): Promise<TimComboAPIResponse<Client>> {
  const queryParams: Record<string, string> = {};
  if (params?.status) queryParams.status = params.status;
  if (params?.tier) queryParams.tier = params.tier;
  if (params?.limit) queryParams.limit = params.limit.toString();
  if (params?.offset) queryParams.offset = params.offset.toString();

  return Make_Authorized_API_Call<Client>(
    API_ENDPOINTS.CLIENTS.LIST,
    'GET',
    undefined,
    Object.keys(queryParams).length > 0 ? { params: queryParams } : undefined
  );
}

/**
 * Get single client by ID
 * GET /api/admin/client?Client_ID=X
 */
export async function getClient(clientId: string): Promise<TimComboAPIResponse<ClientDetails>> {
  return Make_Authorized_API_Call<ClientDetails>(
    API_ENDPOINTS.CLIENTS.GET,
    'GET',
    undefined,
    { params: { Client_ID: clientId } }
  );
}

/**
 * Create new client
 * POST /api/admin/clients
 */
export async function createClient(client: CreateClientRequest): Promise<TimComboAPIResponse<Client>> {
  return Make_Authorized_API_Call<Client>(
    API_ENDPOINTS.CLIENTS.CREATE,
    'POST',
    client
  );
}

/**
 * Update existing client
 * PUT /api/admin/client?Client_ID=X
 */
export async function updateClient(
  clientId: string,
  updates: UpdateClientRequest
): Promise<TimComboAPIResponse<Client>> {
  return Make_Authorized_API_Call<Client>(
    API_ENDPOINTS.CLIENTS.UPDATE,
    'PUT',
    updates,
    { params: { Client_ID: clientId } }
  );
}

/**
 * Delete (soft delete) client
 * DELETE /api/admin/client?Client_ID=X
 */
export async function deleteClient(clientId: string): Promise<TimComboAPIResponse<Client>> {
  return Make_Authorized_API_Call<Client>(
    API_ENDPOINTS.CLIENTS.DELETE,
    'DELETE',
    undefined,
    { params: { Client_ID: clientId } }
  );
}

/**
 * Get companies for a specific client
 * GET /api/companies?Client_ID=...
 */
export async function getClientCompanies(clientId: string): Promise<TimComboAPIResponse<Company>> {
  return Make_Authorized_API_Call<Company>(
    API_ENDPOINTS.COMPANIES.LIST,
    'GET',
    undefined,
    { params: { Client_ID: clientId } }
  );
}
